import React from 'react';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { includes } from 'lodash-es';
import { TertiaryButton, TertiaryButtonGroup } from '@dev/ee-components';
import { push } from 'redux-first-history';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';

import useMobile from '@/services/hooks/useMobile';
import { getAccountAccessLevel } from '@/store/account/actions';
import { useSelectedEntity } from '@/components/Contacts/Group/hooks';
import { UNSENDABLE_SEGMENTS } from '@/services/dictionaries/UnsendableSegmentRules';
import { getConfigStore } from '@/store/config/types';
import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { ContactsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { ALL_CONTACTS_SEGMENT } from '@/services/constants';
import { SetCampaignCreatorOpen } from '@/store/campaigns/actions';

export const HeaderActions = (): JSX.Element => {
    const { t } = useTranslation();
    const { selectedEntity, getDisplayName } = useSelectedEntity();
    const accountAccessLevel = useSelector(getAccountAccessLevel);
    const { isStandalone } = useSelector(getConfigStore);
    const dispatch = useDispatch();
    const canViewCampaigns = accountAccessLevel.includes(AccessLevel.ViewCampaigns);
    const canModifyCampaigns = accountAccessLevel.includes(AccessLevel.ModifyCampaigns);
    const isMobile = useMobile();
    const rule = selectedEntity?.Rule;


    const handleMenuClick = (option: string) => () => {
        const name = safelyEncodeBase64Url(getDisplayName(ALL_CONTACTS_SEGMENT));
        const availableActions = {
            sendcampaign: () => {
                if (isStandalone) {
                    dispatch(SetCampaignCreatorOpen(true, safelyDecodeBase64Url(name)));

                } else {
                    dispatch(
                        push(
                            parseLegacyUrl(
                                ContactsPaths.CampaignCreatorTarget.replace(pathsParams.listName, name),
                                isStandalone
                            )
                        )
                    );
                }
            },
            viewcampaigns: () => dispatch(push(ContactsPaths.ListTargets.replace(pathsParams.listName, name))),
        };
        return availableActions?.[option]?.();
    };

    const options = [
        {
            id: 'send-campaign-button',
            value: 'sendcampaign',
            label: t('sendcampaign'),
            available: canModifyCampaigns && !includes(UNSENDABLE_SEGMENTS, rule),
        },
        {
            id: 'view-campaigns-button',
            value: 'viewcampaigns',
            label: t('viewcampaigns'),
            available: canViewCampaigns && !includes(UNSENDABLE_SEGMENTS, rule),
        },
    ];
    const menuoptions = options
        .filter((option): boolean => option.available)
        .map((option): { label: string; value: string; id: string } => ({
            id: option.id,
            value: option.value,
            label: option.label,
        }));

    return (
        <TertiaryButtonGroup style={{ marginTop: isMobile && '16px' }}>
            {menuoptions.map(
                (option): JSX.Element => (
                    <TertiaryButton
                        id={option.id}
                        key={`header-${option.value}`}
                        className="buttonGroupItem"
                        onClick={handleMenuClick(option.value)}
                        text={t(option.value)}
                    />
                )
            )}
        </TertiaryButtonGroup>
    );
};
